import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Building2,
  ArrowUpRight,
  Check,
  Lock,
  Clock,
  Headphones,
  Shield,
  Globe,
  ChevronDown,
  ChevronUp,
  Zap,
  Users,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Lock, title: 'SSO and SCIM', text: 'Connect your existing identity provider and automate onboarding.' },
  { icon: Clock, title: 'Custom SLAs', text: 'Set, track, and alert on response and resolution targets.' },
  { icon: Headphones, title: 'AI voice', text: 'Make and transcribe calls inside the same inbox.' },
  { icon: Shield, title: 'Audit logs', text: 'Detailed records of every action for compliance.' },
];

const benefits = [
  { icon: Zap, title: '99.99% uptime SLA', description: 'Enterprise-grade reliability with priority support.' },
  { icon: Lock, title: 'SAML SSO support', description: 'Connect your identity provider for secure access.' },
  { icon: Globe, title: 'Global edge network', description: 'Sub-100ms latency with multi-region failover.' },
  { icon: Users, title: 'Dedicated success', description: 'Personal support and custom rollout plans.' },
];

const useCases = [
  { title: 'Global support operations', text: 'Route tickets by region, language, and business unit.' },
  { title: 'Financial services compliance', text: 'Meet audit and data-residency requirements.' },
  { title: 'Large-scale onboarding', text: 'Provision hundreds of agents through your IDP.' },
  { title: 'Government agencies', text: 'Secure, compliant citizen support with audit trails.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Is there an uptime SLA?',
    answer: 'Yes. Enterprise plans include a 99.99% uptime SLA.',
  },
  {
    question: 'Can I use my own identity provider?',
    answer: 'Yes. SAML 2.0 and OIDC SSO are supported.',
  },
  {
    question: 'Do you offer private cloud?',
    answer: 'Yes. Private cloud and on-premise deployment options are available for Enterprise.',
  },
  {
    question: 'What security certifications do you have?',
    answer: 'We are SOC 2 and GDPR-ready with detailed audit logs and compliance controls.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ZuriHubForEnterprisePage() {
  return (
    <>
      <SEO
        title="ZuriHub for Enterprise - Enterprise Support Platform | ZuriHub"
        description="Enterprise-grade security, SLAs, and AI for large support, sales, and operations teams. SSO, SCIM, custom SLAs, 99.99% uptime, private cloud, and AI voice."
        path="/zurihub-for-enterprise"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Building2 className="h-4 w-4" /> Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                The support stack large teams <span className="gradient-text">can control</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Enterprise-grade security, SLAs, and AI. SSO, SCIM, custom SLAs, 99.99% uptime, private cloud, and AI voice for global organizations.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Building2 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/SMS-Feature-EN.avif"
                  alt="ZuriHub for Enterprise showing SSO, SCIM, and enterprise security features"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Enterprise-grade features</h2>
            <p className="section-subtitle mx-auto mt-4">Security, scale, and governance for large teams.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why enterprises choose ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform global support operations.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Security review, custom rollout, SSO setup, and dedicated success. Enterprise deployment designed for your organization.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Shield,
                    title: 'Security review',
                    description: 'Share our SOC 2, GDPR, and architecture docs with your team.',
                  },
                  {
                    icon: Users,
                    title: 'Custom rollout',
                    description: 'We build a deployment plan matched to your org structure.',
                  },
                  {
                    icon: Lock,
                    title: 'SSO and data setup',
                    description: 'Connect identity, map data residency, and set SLAs.',
                  },
                  {
                    icon: Headphones,
                    title: 'Go live with support',
                    description: 'A dedicated success contact helps you launch and scale.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Enterprise features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> SSO and SCIM</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Custom SLAs</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> AI voice</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Audit logs</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Private cloud</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> 99.99% uptime SLA</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Talk to our enterprise team</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Get a custom quote, security review, and rollout plan.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about enterprise</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
